/*
 * @Description: Toast 组件
 * @Author: kadenwang@tencent.com
 * @Date: 2021-11-18 14:35:06
 * @LastEditors: kadenwang@tencent.com
 * @LastEditTime: 2021-12-06 12:49:01
 */

import classNames from 'classnames';
import React, { useEffect, useState, createRef } from 'react';
import * as ReactDOM from 'react-dom';

import './index.less';

export interface IToast {
  type: 'info' | 'error' | 'success' | 'loading';
  duration: number;
  msg: string;
  onDisapperCallBack?: () => void;
  opacity?: number;
}

export interface ToastHandle {
  /** 手动调用消失方法 */
  setDisappear: (hidden: boolean) => void;
}

const ToastBase: React.FC<IToast> = (props: React.PropsWithChildren<IToast>) => {
  const { opacity, msg, type, duration, onDisapperCallBack } = props;
  const [disappear, setDisappear] = useState(false);
  const divRef = React.createRef<HTMLDivElement>();

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (duration) {
      timer = setTimeout(() => {
        setDisappear(true);
      }, duration);
    }

    return () => {
      clearTimeout(timer);
    };
  }, [duration]);

  const handleContentAnimationEnd = () => {
    if (disappear && onDisapperCallBack) {
      onDisapperCallBack();
    }
  };

  return (
    <div ref={divRef} className={classNames('login-v2-toast', 'toast-box')} style={{ backgroundColor: `rgba(0, 0, 0, ${opacity})` }}>
      <div className={classNames('toast-content', { disappear })} onAnimationEnd={() => handleContentAnimationEnd()}>
        <div className={classNames('toast-icon', type)} />
        <div className="toast-msg">{msg}</div>
      </div>
    </div>
  );
};

type IExportToast = typeof ToastBase & {
  info: (msg: string, optional?: Partial<IToast>) => ToastHandle;
  error: (msg: string, optional?: Partial<IToast>) => ToastHandle;
  success: (msg: string, optional?: Partial<IToast>) => ToastHandle;
  loading: (msg: string, optional?: Partial<IToast>) => ToastHandle;
};

export const Toast = ToastBase as IExportToast;

const commonToast = (type: IToast['type'], msg: string, optional?: Partial<IToast>): ToastHandle => {
  const div = document.createElement('div');
  document.body.appendChild(div);
  const toastRef = createRef<ToastHandle>();

  const mergeEdProps = {
    /** 默认值 */
    ...{ duration: 2000, opacity: 0 },
    /** 传入值 */
    ...optional,
    ...{
      msg,
      type,
      onDisapperCallBack: () => ReactDOM.unmountComponentAtNode(div),
    },
  };

  ReactDOM.render(
    (
      <Toast
        {...mergeEdProps}
      />
    ),
    div,
  );

  return toastRef.current as ToastHandle;
};

Toast.info = (msg: string, optional?: Partial<IToast>) => commonToast('info', msg, optional);
Toast.error = (msg: string, optional?: Partial<IToast>) => commonToast('error', msg, optional);
Toast.success = (msg: string, optional?: Partial<IToast>) => commonToast('success', msg, optional);
Toast.loading = (msg: string, optional?: Partial<IToast>) => commonToast('loading', msg, optional);
